import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectAuth } from '../../../services/auth/selectors'
import { selectUserLanguagePrimary } from '../../../services/user/index'
import { SeedQuestionCategory } from '../../../graphql-types'
import { useAllTarotCategoryQuestionsQuery } from '../queries.generated'
import { extractTagMeta } from './seedQuestionTagUtils'
import type { TarotSeedQuestion } from './useTarotSeedQuestions'

/**
 * Subset of `SeedQuestionCategory` values the Tarot UI surfaces.  The full
 * enum also includes PracticeYoga / PracticeMeditation / PracticeBreathwork /
 * NonPractice, but those are wellness-flow categories handled elsewhere.
 */
export type TarotSeedCategory =
  | SeedQuestionCategory.LoveRelationships
  | SeedQuestionCategory.WorkMoney
  | SeedQuestionCategory.PersonalDevelopment
  | SeedQuestionCategory.General

/**
 * `Pick` narrows the full `Record<SeedQuestionCategory, …>` to just the keys
 * this hook returns, so the memoized literal is structurally complete and
 * downstream `questionsByCategory[…]` lookups stay exhaustive.
 */
export type QuestionsByCategory = Pick<
  Record<SeedQuestionCategory, TarotSeedQuestion[]>,
  TarotSeedCategory
>

interface UseAllTarotCategoryQuestionsResult {
  questionsByCategory: QuestionsByCategory
  loading: boolean
}

interface RawList {
  seedQuestions: Array<{ id: string; question: string; tags: string[] }>
}

const mapList = (list: RawList | undefined): TarotSeedQuestion[] => (
  list?.seedQuestions?.map((sq) => ({
    id: sq.id,
    question: sq.question,
    tagMeta: extractTagMeta(sq.tags),
  })) ?? []
)

export const useAllTarotCategoryQuestions = (): UseAllTarotCategoryQuestionsResult => {
  const auth = useSelector(selectAuth)
  const language = useSelector(selectUserLanguagePrimary)

  const { data, loading } = useAllTarotCategoryQuestionsQuery({
    variables: { language },
    context: { jwt: auth.get('jwt') },
    fetchPolicy: 'cache-and-network',
  })

  const questionsByCategory = useMemo<QuestionsByCategory>(() => ({
    [SeedQuestionCategory.LoveRelationships]: mapList(data?.loveRelationships),
    [SeedQuestionCategory.WorkMoney]: mapList(data?.workMoney),
    [SeedQuestionCategory.PersonalDevelopment]: mapList(data?.personalDevelopment),
    [SeedQuestionCategory.General]: mapList(data?.general),
  }), [data])

  return { questionsByCategory, loading }
}
